import { hash } from '@ember/helper';
import { get } from '@ember/object';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { not } from 'ember-truth-helpers';
import type { Identifier } from 'garaje/models/user-document-template';

interface UserDocumentTemplateLike {
  identifier: Identifier;
}

interface EmployeesDocumentReviewDropdownOptionSignature {
  Args: {
    // The production caller derives this via `.mapBy('userDocument.userDocumentTemplate').uniqBy('identifier')`
    // across two AsyncBelongsTo hops. `mapBy` yields `null`/`undefined` for unloaded
    // relationships, so the array can be sparse. Loaded entries arrive as PromiseProxy-backed
    // models; reads route through Ember's `get()` so the proxy's `unknownProperty` trap
    // fires (direct property access on an `ObjectProxy` returns `undefined` rather than the
    // proxied attribute).
    reviewableUserDocumentTemplates: readonly (UserDocumentTemplateLike | null | undefined)[];
    userDocumentTemplate: UserDocumentTemplateLike | null | undefined;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLAnchorElement;
}

export default class EmployeesDocumentReviewDropdownOption extends Component<EmployeesDocumentReviewDropdownOptionSignature> {
  get isUserDocumentTemplateEnabled(): boolean {
    const { userDocumentTemplate, reviewableUserDocumentTemplates } = this.args;
    if (userDocumentTemplate == null) return false;
    // Route through Ember's `get()` so an `ObjectProxy` from an AsyncBelongsTo resolves its
    // `identifier` via `unknownProperty` (direct `.identifier` access on the proxy returns
    // `undefined`). `get()` itself asserts on null/undefined input in dev mode, so each entry
    // is null-guarded before the call.
    // eslint-disable-next-line ember/no-get
    const targetIdentifier = get(userDocumentTemplate, 'identifier');
    return reviewableUserDocumentTemplates.some(
      // eslint-disable-next-line ember/no-get
      (t) => t != null && get(t, 'identifier') === targetIdentifier,
    );
  }

  <template>
    <LinkTo
      @route="employees.document-review.index"
      @query={{hash type=@userDocumentTemplate.identifier}}
      @disabled={{not this.isUserDocumentTemplateEnabled}}
      aria-disabled="{{if this.isUserDocumentTemplateEnabled 'false' 'true'}}"
      class="no-underline hover:no-underline
        {{if this.isUserDocumentTemplateEnabled 'text-black' 'text-carbon-40 cursor-default'}}"
      data-test-review-documents-cta={{@userDocumentTemplate.identifier}}
      ...attributes
    >
      {{yield}}
    </LinkTo>
  </template>
}
